import React from 'react'
import { Link } from 'gatsby'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { get, first } from 'lodash'
import {
  SkipNavLink,
  SkipNavContent,
} from '@reach/skip-nav'
import Navigation from './Navigation'
import Footer from './Footer'
import { getLocale } from '../utils/i18n-helpers'
import Search from './Search'

const printLocale = (key) =>
  ({
    'fr-ca': 'Français',
    'en-ca': 'English',
  }[key])

const findIn = (target) => (input) => {
  const exec = (obj = {}) =>
    typeof obj === 'object' && obj !== null
      ? Object.entries(obj).reduce((acc, [key, value]) => {
          if (acc) return acc
          if (key === target) return value
          return exec(value) || acc
        }, '')
      : undefined

  return exec(input)
}

export const findSeo = findIn('seo')

const CurrentDocumentLanguage = (props) => {
  const lang = String(getLocale() || 'en').split('-')[0]
  const fs = findSeo(props)
  const seo = Array.isArray(fs) ? first(fs) : fs
  const content = get(seo, 'description.description')
  const title = get(seo, 'title')

  return (
    <Helmet
      defer={false}
      meta={[
        {
          name: 'description',
          content,
        },
      ]}
      title={title}
    >
      <html lang={lang} />
    </Helmet>
  )
}

export const AlternativeLocaleCannonicals = ({
  hreflang,
}) => {
  const { t } = useTranslation('labels')

  return hreflang && Array.isArray(hreflang.nodes)
    ? hreflang.nodes.map((href) => {
        const code = href.node_locale.toLowerCase()
        const path = href.relativePath || `/${code}`

        return (
          <React.Fragment key={code}>
            <Helmet>
              <link
                rel="alternate"
                href={[
                  'https://www.canadarubbergroup.com',
                  path,
                ].join('')}
                hrefLang={code}
              />
            </Helmet>
            <Button
              component={Link}
              to={path}
              color="inherit"
            >
              {t('trans', {
                code: printLocale(code),
              })}
            </Button>
          </React.Fragment>
        )
      })
    : null
}

const withGlobals = (Component) => {
  const Globals = ({
    data: { hreflang, ...rest },
    pageContext,
  }) => (
    <Box overflow="hidden">
      <SkipNavLink className="skipLink" />
      <Navigation>
        <Box
          bgcolor="primary.main"
          color="primary.contrastText"
          px={2}
          position="relative"
          textAlign="right"
          zIndex={100}
        >
          <CurrentDocumentLanguage {...rest} />
          <AlternativeLocaleCannonicals
            hreflang={hreflang}
          />
          <Search />
        </Box>
      </Navigation>
      <SkipNavContent />
      <Box
        component={
          pageContext?.contentful_id !==
          'wMUu00or8N4EXHm2zKPjk'
            ? 'main'
            : undefined
        }
      >
        <Component {...rest} pageContext={pageContext} />
      </Box>
      <Footer />
    </Box>
  )

  Globals.propTypes = {
    data: PropTypes.shape({
      hreflang: PropTypes.shape({
        // eslint-disable-next-line
        nodes: PropTypes.array,
      }),
    }).isRequired,
    // eslint-disable-next-line
    pageContext: PropTypes.object,
  }

  return Globals
}

export const makeTemplateWithGlobal = (Component) => (
  nodes = [],
  args = {}
) => (props) => {
  const Template = withGlobals(Component)

  return (
    <Template
      {...props}
      data={{
        hreflang: {
          nodes,
        },
        ...args,
      }}
    />
  )
}

export default withGlobals
